// import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext } from 'react';
import { noop } from 'lodash';
import writtenNumber from 'written-number';
import {
    LocaleService,
    WindowProxy,
    EmailService,
    ConfigsService,
    ContentZonesService,
    CustomHooks,
} from '@sm360/widget-commons-core-services';
import {
    ModalService,
    AccountService,
    EVENTS,
    AnalyticsService,
    TRACKING_TAGS as TAGS,
} from '@sm360/widget-commons-general';
import { PurchaseMethods } from '@sm360/widget-commons-vehicle';
import { logger } from '@sm360/logger';
import 'trade-in/components/InstantEstimateWrapper/Steps/VehicleAppraisal/VehicleAppraisal.scss';
import TRADE_OPTIONS, { TRADE_OPTIONS_KEY } from '../../../../constants/tradeOptions.js';
import { tradeOnPurchaseImg, sellYourVehicleImg, notSureImg } from '../../../../constants/assetsUrl.js';
import AppraisalCard from './Components/AppraisalCard.jsx';
import OptionCard from './Components/OptionCard.jsx';
import NewTradeInContext from '../../../../NewTradeInContext.js';
import ActionButton from '../../Components/ActionButton/ActionButton.jsx';
import { getLocaleFormat } from '../../Utils/utils.js';

const { useScript } = CustomHooks;
const { PurchaseMethodsActions } = PurchaseMethods;

const getCZ = (name) => {
    return ContentZonesService.getContentZone('widget.tradein.teaser', name)
        ? ContentZonesService.getContentZone('widget.tradein.teaser', name).getValue()
        : undefined;
};

const trackSelfInspect = () => {
    AnalyticsService.gaTrigger(TAGS.TRADE_IN_SELF_INSPECT(WindowProxy.location().pathname));
};

const VehicleAppraisal = ({
    tradeInVehicle,
    onTradeInVehicleUpdated,
    onPreviousStep,
    closeModal,
    onMakeAppointment,
    account,
    handleEstimateChange,
}) => {
    const [selectedOption, setSelectedOption] = useState(tradeInVehicle?.selectedTradeOption || 0);
    const [fetching, setFetching] = useState(false);
    const { fieldsLocked, setFieldsLocked } = useContext(NewTradeInContext);

    // Get content zone option texts
    const CZ_APPRAISAL_DESCRIPTION = getCZ('appraisal.description');
    const CZ_TRADE_ON_PURCHASE_DESCRIPTION = getCZ('appraisal.trade.on.purchase.description');
    const CZ_SELL_YOUR_VEHICLE_DESCRIPTION = getCZ('appraisal.sell.your.vehicle.description');
    const CZ_NOT_SURE_DESCRIPTION = getCZ('appraisal.not.sure.description');

    // Get content zone option images
    const CZ_TRADE_ON_PURCHASE_IMG = getCZ('appraisal.trade.on.purchase.image') || tradeOnPurchaseImg;
    const CZ_SELL_YOUR_VEHICLE_IMG = getCZ('appraisal.sell.your.vehicle.image') || sellYourVehicleImg;
    const CZ_NOT_SURE_IMG = getCZ('appraisal.not.sure.image') || notSureImg;

    /* Script requested by Dilawri for having Self-Inspect screen */
    useScript('https://dgoc-pave-app-5ukghzz6xq-uk.a.run.app/js/widget.js');

    // Scroll to options/appraisal amount when selected option changes
    // Wait for 300ms to let the open/collapse animation finish
    useEffect(
        () => {
            const currentOption = document.getElementById('appraisal-options');
            if (currentOption) setTimeout(() => currentOption.scrollIntoView({ behavior: 'smooth' }), 300);
        },
        [selectedOption]
    );

    // Lock all previous screens fields
    useEffect(() => {
        if (!fieldsLocked) {
            setFieldsLocked(true);
        }
    }, []);
    const SignUpModal = ModalService.get('login/SignUpModal');

    const dispatchTradeInCreatedEvent = () => {
        const $window = WindowProxy.get();
        if ($window) {
            $window.document.dispatchEvent(new Event(EVENTS.TRADEIN_CREATED));
        }
    };

    const saveTradeIn = ({ option, saveInfoOnly = false, existingAccount = false }) => {
        handleEstimateChange(tradeInVehicle.estimation.amount, option || selectedOption, existingAccount)
            .then(({ leadId }) => {
                // Call to add only basic info into already generated lead
                if (!saveInfoOnly) {
                    dispatchTradeInCreatedEvent();
                    PurchaseMethodsActions.setTradeIn(tradeInVehicle.estimation);
                }

                // If option is "Sell Your vehicle", send an email cheque
                if (leadId && option === TRADE_OPTIONS.SELL_VEHICLE) {
                    const amount = tradeInVehicle?.estimation?.amount;
                    const locale = LocaleService.getSimpleLocale();

                    EmailService.sendTradeInChequeEmail({
                        websiteId: ConfigsService.getWebsiteId(),
                        locale,
                        leadId,
                        clientName: `${tradeInVehicle?.contact?.firstname} ${tradeInVehicle?.contact?.lastname}`,
                        year: tradeInVehicle?.year,
                        make: tradeInVehicle?.make,
                        model: tradeInVehicle?.model,
                        trim: tradeInVehicle?.trim,
                        vin: tradeInVehicle?.vin,
                        exteriorColor: tradeInVehicle?.exteriorColor?.description,
                        amount: getLocaleFormat(amount, locale),
                        amountInLetters: typeof amount === 'number' ? writtenNumber(amount, { lang: locale }) : '',
                        conditionFormList: tradeInVehicle?.formattedConditionReport,
                        toEmail: tradeInVehicle?.contact?.email,
                        organizationUnitName: ConfigsService.getOrganizationUnitName() || '',
                        organizationUnitPhone: ConfigsService.getOrganizationUnitPhone() || '',
                        scheduleMonday: ConfigsService.getScheduleMonday(),
                        scheduleTuesday: ConfigsService.getScheduleTuesday(),
                        scheduleWednesday: ConfigsService.getScheduleWednesday(),
                        scheduleThursday: ConfigsService.getScheduleThursday(),
                        scheduleFriday: ConfigsService.getScheduleFriday(),
                        scheduleSaturday: ConfigsService.getScheduleSaturday(),
                        scheduleSunday: ConfigsService.getScheduleSunday(),
                        websiteDomain: ConfigsService.getWebsiteDomain() || '',
                        websiteLogo: ConfigsService.getWebsiteLogo() || '',
                        websiteFacebookUrl: ConfigsService.getWebsiteFacebookUrl() || '',
                        websiteInstagramUrl: ConfigsService.getWebsiteInstagramUrl() || '',
                        websiteTwitterUrl: ConfigsService.getWebsiteTwitterUrl() || '',
                        websiteYoutubeUrl: ConfigsService.getWebsiteYoutubeUrl() || '',
                    }).catch((e) => {
                        logger.error(`Failed sending an email cheque for leadId=${leadId}`, e);
                    });
                } else {
                    logger.error(
                        "Couldn't send Cheque Email for TradeIn: Sell Your Vehicle: leadId is not defined because lead hasn't been updated"
                    );
                }
            })
            .finally(() => {
                setFetching(false);
            });
    };

    const loginToSave = () => {
        [SignUpModal].forEach((modal) => {
            modal.addCallbacks({
                onClose: () => {
                    setTimeout(() => {
                        saveTradeIn({ existingAccount: true });
                    }, 1000);
                    return true;
                },
            });
        });
        SignUpModal.open({ context: 'widget.login.signup.information', account, hasConfirmation: true });
        closeModal();
    };

    const handleOptionSelection = (option) => {
        if (!selectedOption > 0) {
            // Save selected option in upper parent's state (tradeInVehicle)
            onTradeInVehicleUpdated({ ...tradeInVehicle, selectedTradeOption: option });
            setSelectedOption(option);
            setFetching(true);
            if (AccountService.isAuthenticated()) {
                saveTradeIn({ option, existingAccount: true });
            } else {
                /** Asynchronous lead creation force to add timeout between two tradeIn lead call
                 * First tradeIn lead call when submit the estimation
                 * Second call her by selecting the trade option
                 * Removing the timeout would create an another tradeIn lead on second call in place update the first tradeIn lead
                 * */
                setTimeout(() => {
                    saveTradeIn({ option, saveInfoOnly: true });
                }, 5000);
            }
        }
    };

    return (
        <div className="appraisal-form">
            <form className="trade-in-form" onSubmit={noop}>
                <section className="appraisal-form--title">
                    <h3>{LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.TITLE')}</h3>
                    <p>
                        {CZ_APPRAISAL_DESCRIPTION ||
                            LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.DESCRIPTION')}
                    </p>
                </section>
                <section className="appraisal-form--options clearfix">
                    <p className="sr-text">
                        {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.SELECT_ONE')}
                    </p>
                    <div className="appraisal-form-mobile-buttons vehicle-buttons-container">
                        <div className="vehicle-buttons">
                            <ActionButton
                                next
                                noPrefix
                                noArrow
                                noSubmit
                                selected={selectedOption === TRADE_OPTIONS.TRADE_PURCHASE}
                                onClick={() => handleOptionSelection(TRADE_OPTIONS.TRADE_PURCHASE)}
                                disabled={selectedOption > 0}
                            >
                                {LocaleService.t(
                                    'TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.TRADE_ON_PURCHASE.TITLE'
                                )}
                            </ActionButton>
                            <ActionButton
                                next
                                noPrefix
                                noArrow
                                noSubmit
                                selected={selectedOption === TRADE_OPTIONS.SELL_VEHICLE}
                                onClick={() => handleOptionSelection(TRADE_OPTIONS.SELL_VEHICLE)}
                                disabled={selectedOption > 0}
                            >
                                {LocaleService.t(
                                    'TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.SELL_VEHICLE.TITLE'
                                )}
                            </ActionButton>
                            <ActionButton
                                next
                                noPrefix
                                noArrow
                                noSubmit
                                selected={selectedOption === TRADE_OPTIONS.NOT_SURE}
                                onClick={() => handleOptionSelection(TRADE_OPTIONS.NOT_SURE)}
                                disabled={selectedOption > 0}
                            >
                                {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.NOT_SURE.TITLE')}
                            </ActionButton>
                        </div>
                    </div>
                    <div className="appraisal-form-options-container" id="appraisal-options">
                        <OptionCard
                            img={CZ_TRADE_ON_PURCHASE_IMG}
                            title={LocaleService.t(
                                'TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.TRADE_ON_PURCHASE.TITLE'
                            )}
                            desc={
                                CZ_TRADE_ON_PURCHASE_DESCRIPTION ||
                                LocaleService.t(
                                    'TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.TRADE_ON_PURCHASE.DESCRIPTION'
                                )
                            }
                            onClick={() =>
                                // If authenticated, do tradeIn lead update with basic info
                                handleOptionSelection(TRADE_OPTIONS.TRADE_PURCHASE)
                            }
                            isSelected={selectedOption === TRADE_OPTIONS.TRADE_PURCHASE}
                            disabled={selectedOption > 0}
                        />
                        <OptionCard
                            img={CZ_SELL_YOUR_VEHICLE_IMG}
                            title={LocaleService.t(
                                'TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.SELL_VEHICLE.TITLE'
                            )}
                            desc={
                                CZ_SELL_YOUR_VEHICLE_DESCRIPTION ||
                                LocaleService.t(
                                    'TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.SELL_VEHICLE.DESCRIPTION'
                                )
                            }
                            onClick={() => handleOptionSelection(TRADE_OPTIONS.SELL_VEHICLE)}
                            isSelected={selectedOption === TRADE_OPTIONS.SELL_VEHICLE}
                            disabled={selectedOption > 0}
                        />
                        <OptionCard
                            img={CZ_NOT_SURE_IMG}
                            title={LocaleService.t(
                                'TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.NOT_SURE.TITLE'
                            )}
                            desc={
                                CZ_NOT_SURE_DESCRIPTION ||
                                LocaleService.t(
                                    'TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.NOT_SURE.DESCRIPTION'
                                )
                            }
                            onClick={() => handleOptionSelection(TRADE_OPTIONS.NOT_SURE)}
                            isSelected={selectedOption === TRADE_OPTIONS.NOT_SURE}
                            disabled={selectedOption > 0}
                        />
                    </div>
                </section>
                <AppraisalCard
                    value={tradeInVehicle?.estimation?.amount}
                    onMakeAppointment={onMakeAppointment}
                    onSaveEstimate={loginToSave}
                    onSelfInspect={trackSelfInspect}
                    isActive={selectedOption > 0}
                    fetching={fetching}
                    selectedOption={LocaleService.t(
                        `TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.${TRADE_OPTIONS_KEY[selectedOption]}.TITLE`
                    )}
                    tradeInVehicle={tradeInVehicle}
                />

                <section className="vehicle-buttons-container clearfix" data-test="trade-in.vehicle-form.submit">
                    <div className="vehicle-buttons">
                        <ActionButton onClick={() => onPreviousStep()} disabled={fetching}>
                            {LocaleService.t('TRADE_IN.SECTIONS.CONTACT_INFO')}
                        </ActionButton>
                        <ActionButton noPrefix noArrow onClick={() => closeModal()} disabled={fetching}>
                            {LocaleService.t('TRADE_IN.BUTTONS.KEEP_BROWSING')}
                        </ActionButton>
                    </div>
                </section>
            </form>
        </div>
    );
};

VehicleAppraisal.displayName = 'VehicleAppraisal';

export default VehicleAppraisal;
